import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link } from "react-router-dom"
import { CreateOperationsAdminVenueDocumentAction, UpdateOperationsAdminVenueDocumentAction } from "src/components/operations/administrator/documents/OperationsAdminVenueDocumentActions"
import { AssetThumbnail } from "src/components/ui/AssetThumbnail"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { Document_Status_Enum, DocumentFragment, Documents_Bool_Exp } from "src/state/gql/_types"
import { formatDate } from "src/utils/dates"
import { formatFileSize } from "src/utils/string"

export function OperationsAdminVenueDocuments() {

    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.documents)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
        setMeta,
        selectItem
    } = useActions().documents
    const { view } = useAppState(state => state.ui)
    const { tenant_id: currentTenantId } = useAppState(state => state.auth)

    const {
        filters,
        filterParams
    } = usePageFilters<Documents_Bool_Exp['_and'], DocumentFragment>({
        baseFilters: [{
            venue_id: { _eq: currentTenantId }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Document_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFiltersDisableDefaultAll: true,
        clickFilterField: 'status',
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: Document_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: Document_Status_Enum.Inactive } }] },
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<DocumentFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Preview',
            cell: row => {
                return <AssetThumbnail url={row.row.original.url || ''}/> 
            }
        }),
        columnHelper.accessor(row => row.document_status.display, {
            header: 'Status'
        }),
        columnHelper.accessor(row => row.document_type.display, {
            header: 'Type'
        }),
        columnHelper.accessor('filename', {
            header: 'Filename'
        }),
        columnHelper.accessor('filetype', {
            header: 'File Type'
        }),
        columnHelper.accessor('size', {
            header: 'Size',
            cell: row => formatFileSize(parseInt(row.getValue() || '0'))
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {}

    const handleMetaChange = (meta: SetMetaInput<DocumentFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/documents/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.filename}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<DocumentFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Documents`}
                    renderAction={
                        <Group>
                            <UpdateOperationsAdminVenueDocumentAction />
                            <CreateOperationsAdminVenueDocumentAction />
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Documents' />)}
        </PageLayout>
    )

}